import * as Phaser from 'phaser';
import { getStateCallbacks } from 'colyseus.js';
import { gameClient }         from '../network/GameClient';
import { pendingJoin }        from '../pendingJoin';
import { ShipSprite }         from '../entities/ShipSprite';
import { ProjectileSprite }   from '../entities/ProjectileSprite';
import { StationSprite }      from '../entities/StationSprite';
import { PowerupSprite }      from '../entities/PowerupSprite';
import type { InputMessage, PlayerState, ProjectileState, SpaceStationState, BeamFiredPayload, ShieldHitPayload, HullHitPayload, ShipCollisionPayload, WallCollisionPayload, ShipId, PowerupCollectedPayload } from '@rttv/shared';
import { IS_TOUCH, touchState, mountTouchControls, unmountTouchControls, getHudWidth } from '../touch/TouchControls';
import {
  WORLD_WIDTH,
  WORLD_HEIGHT,
  BORDER_THICKNESS,
  ServerEvents,
  PRIMARY_WEAPONS,
  SECONDARY_WEAPONS,
  ARENAS,
  generateObstacles,
} from '@rttv/shared';
import type { PowerupState } from '@rttv/shared';
import { getShipVisual, SHIP_VISUALS } from '../shipVisuals';

const BASE     = '/assets/kenney/space-shooter-remastered';
const BASE_EXT = '/assets/kenney/space-shooter-extension';
const RITV_WORLD = '/assets/ritv/v1/world';
const RITV_BACKGROUNDS = '/assets/ritv/v1/backgrounds';
const RITV_WORLD_TEXTURES = [
  'bg_void',
  'ritv_asteroid_small',
  'ritv_asteroid_large',
  'ritv_asteroid_giant',
  'ritv_repair_station',
  'ritv_repair_pad',
  'ritv_derelict',
] as const;

export class GameScene extends Phaser.Scene {
  private ships       = new Map<string, ShipSprite>();
  private projectiles = new Map<string, ProjectileSprite>();
  private stations    = new Map<string, StationSprite>();
  private powerups    = new Map<string, PowerupSprite>();
  private keyW!:       Phaser.Input.Keyboard.Key;
  private keyA!:       Phaser.Input.Keyboard.Key;
  private keyS!:       Phaser.Input.Keyboard.Key;
  private keyD!:       Phaser.Input.Keyboard.Key;
  private keySpace!:   Phaser.Input.Keyboard.Key;
  private keyF!:       Phaser.Input.Keyboard.Key;
  private localId       = '';
  private localShield   = -1;
  private thrusterSound: Phaser.Sound.BaseSound | null = null;
  private inputSeq    = 0;
  private connected   = false;
  /** Faint aim-direction line drawn for the local beam-weapon ship. */
  private aimReticle!: Phaser.GameObjects.Graphics;
  /** Whether the local player's primary weapon is a beam (turret). Set on join. */
  private localIsBeam    = false;
  private lastAimAngle   = 0;
  private aimDirty       = true; // force first draw

  constructor() {
    super({ key: 'GameScene' });
  }

  preload() {
    // Loading bar
    const cx = this.scale.width  / 2;
    const cy = this.scale.height / 2;
    const loadingTrack = this.add.rectangle(cx, cy, 402, 26, 0x1a1a2e);
    const bar = this.add.rectangle(cx - 200, cy, 0, 22, 0x4488ff).setOrigin(0, 0.5);
    const loadingLabel = this.add.text(cx, cy + 22, 'RETURN TO THE VOID', {
      fontSize: '14px', color: '#4488ff',
    }).setOrigin(0.5, 0).setAlpha(0.7);

    const onLoadProgress = (v: number) => bar.setSize(400 * v, 22);
    this.load.on('progress', onLoadProgress);
    this.load.once('complete', () => {
      this.load.off('progress', onLoadProgress);
      loadingTrack.destroy();
      bar.destroy();
      loadingLabel.destroy();
    });

    // Spritesheet atlas (all game sprites)
    this.load.atlasXML(
      'sheet',
      `${BASE}/Spritesheet/sheet.png`,
      `${BASE}/Spritesheet/sheet.xml`,
    );
    // Space Shooter Extension atlas (stations, giant meteors, buildings)
    this.load.atlasXML(
      'ext',
      `${BASE_EXT}/Spritesheet/spaceShooter2_spritesheet.png`,
      `${BASE_EXT}/Spritesheet/spaceShooter2_spritesheet.xml`,
    );
    // Void Salvage '99 renders — standalone textures keep their clean alpha
    // edges and allow linear filtering without disturbing the legacy atlases.
    for (const visual of Object.values(SHIP_VISUALS)) {
      this.load.image(visual.textureKey, visual.assetUrl);
    }
    this.load.image('bg_void', `${RITV_BACKGROUNDS}/void-backdrop.webp`);
    this.load.image('ritv_asteroid_small', `${RITV_WORLD}/asteroid-small.png`);
    this.load.image('ritv_asteroid_large', `${RITV_WORLD}/asteroid-large.png`);
    this.load.image('ritv_asteroid_giant', `${RITV_WORLD}/asteroid-giant.png`);
    this.load.image('ritv_repair_station', `${RITV_WORLD}/repair-station.png`);
    this.load.image('ritv_repair_pad', `${RITV_WORLD}/repair-pad.png`);
    this.load.image('ritv_derelict', `${RITV_WORLD}/derelict.png`);

    // SFX — custom waves
    const SND = '/assets/sounds';
    this.load.audio('snd_fighter_primary', `${SND}/fighter_primary.wav`);
    this.load.audio('snd_tank_primary',    `${SND}/tank_primary.wav`);
    this.load.audio('snd_torp_launch',     `${SND}/torp_launch.wav`);
    this.load.audio('snd_missile_launch',  `${SND}/missile_launch.wav`);
    // SFX — Kenney bonus
    const SCI = '/assets/kenney/sci-fi-sounds/Audio';
    this.load.audio('sfx_laser1',        `${BASE}/Bonus/sfx_laser1.ogg`);
    this.load.audio('sfx_ufo_primary',   `${SCI}/laserSmall_001.ogg`);
    this.load.audio('sfx_shield_up',     `${BASE}/Bonus/sfx_shieldUp.ogg`);
    this.load.audio('sfx_shield_dn',     `${BASE}/Bonus/sfx_shieldDown.ogg`);
    this.load.audio('sfx_zap',           `${BASE}/Bonus/sfx_zap.ogg`);
    this.load.audio('sfx_twoTone',       `${BASE}/Bonus/sfx_twoTone.ogg`);
    // SFX — Kenney sci-fi sounds
    this.load.audio('sfx_explosion_0',   `${SCI}/lowFrequency_explosion_000.ogg`);
    this.load.audio('sfx_explosion_1',   `${SCI}/lowFrequency_explosion_001.ogg`);
    this.load.audio('sfx_shield_hit',    `${SCI}/laserSmall_000.ogg`);
    this.load.audio('sfx_hull_hit',      `${SCI}/impactMetal_004.ogg`);
    this.load.audio('sfx_collision',     `${SCI}/impactMetal_002.ogg`);
    this.load.audio('snd_thruster_small',    `${SCI}/spaceEngineSmall_000.ogg`);
    this.load.audio('snd_thruster_circular', `${SCI}/engineCircular_000.ogg`);
    this.load.audio('snd_thruster_large',    `${SCI}/spaceEngineLarge_000.ogg`);

  }

  create() {
    // The high-resolution pre-rendered hulls want smooth downsampling. Keep the
    // legacy atlases nearest-neighbour so their tightly packed frames do not bleed.
    for (const visual of Object.values(SHIP_VISUALS)) {
      this.textures.get(visual.textureKey).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }
    for (const texture of RITV_WORLD_TEXTURES) {
      this.textures.get(texture).setFilter(Phaser.Textures.FilterMode.LINEAR);
    }

    // A single painted field avoids the obvious repetition of the old 256px tile
    // and gives the high-resolution ships and world props a coherent backdrop.
    this.add
      .image(WORLD_WIDTH / 2, WORLD_HEIGHT / 2, 'bg_void')
      .setDisplaySize(WORLD_WIDTH, WORLD_HEIGHT)
      .setDepth(-2);

    // World boundary outline
    this.add
      .rectangle(WORLD_WIDTH / 2, WORLD_HEIGHT / 2, WORLD_WIDTH, WORLD_HEIGHT)
      .setStrokeStyle(4, 0xa85d28, 0.72)
      .setFillStyle(0, 0);

    // Asteroid border ring
    this.createAsteroidBorder();

    // Camera — viewport constrained to the left of the HUD panel
    const screenW = this.scale.width || window.innerWidth;
    const screenH = this.scale.height || window.innerHeight;
    const hudW    = getHudWidth(screenW);
    const gameW   = screenW - hudW;
    const gameH   = screenH;
    this.cameras.main.setViewport(0, 0, gameW, gameH);
    this.cameras.main.setBounds(0, 0, WORLD_WIDTH, WORLD_HEIGHT);
    if (IS_TOUCH) this.cameras.main.setZoom(0.65);

    // Input — WASD move, Space fire (desktop); virtual joystick + buttons (touch)
    const kb    = this.input.keyboard!;
    this.keyW   = kb.addKey(Phaser.Input.Keyboard.KeyCodes.W);
    this.keyA   = kb.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyS   = kb.addKey(Phaser.Input.Keyboard.KeyCodes.S);
    this.keyD   = kb.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    this.keySpace = kb.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.keyF     = kb.addKey(Phaser.Input.Keyboard.KeyCodes.F);
    // Prevent Space from scrolling the page
    kb.addCapture(Phaser.Input.Keyboard.KeyCodes.SPACE);
    // Disable right-click context menu so right-click fires secondary weapon
    this.input.mouse?.disableContextMenu();

    if (IS_TOUCH) mountTouchControls(hudW);

    // Resize handler — recalculates camera viewport and touch overlay when the
    // device rotates or the browser window changes size.
    this.scale.on('resize', () => {
      const sw = this.scale.width  || window.innerWidth;
      const sh = this.scale.height || window.innerHeight;
      const hw = getHudWidth(sw);
      this.cameras.main.setViewport(0, 0, sw - hw, sh);
      if (IS_TOUCH) {
        unmountTouchControls();
        mountTouchControls(hw);
      }
    });

    // Aim reticle — persistent Graphics object redrawn each frame for beam ships
    this.aimReticle = this.add.graphics();
    this.aimReticle.setDepth(8); // just below ships

    // Launch UIScene in parallel
    this.scene.launch('UIScene');

    // Connect to server — use lobby selection when available, fall back to URL params for dev
    const params      = new URLSearchParams(window.location.search);
    const displayName = pendingJoin?.displayName ?? params.get('name') ?? `Pilot_${Math.floor(Math.random() * 9000) + 1000}`;
    const shipId      = pendingJoin?.shipId      ?? params.get('shipId') ?? 'fighter';
    const roomId      = pendingJoin?.roomId      ?? params.get('roomId') ?? null;

    const joinPromise = roomId
      ? gameClient.joinById(roomId, displayName, shipId)
      : gameClient.joinOrCreate(displayName, shipId);

    joinPromise.then((room) => {
      this.localId   = room.sessionId;
      this.connected = true;

      this.thrusterSound = this.sound.add(getShipVisual(shipId).thrusterSound, { loop: true, volume: 0.06 });

      // Spawn arena obstacles using density from room metadata
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const meta = (room as any).metadata as { arenaName?: string; obstacleDensity?: number } | null;
      this.createArenaObstacles(meta?.arenaName ?? 'The Void', meta?.obstacleDensity);

      const $ = getStateCallbacks(room);

      // ------------------------------------------------------------------
      // Player added
      // ------------------------------------------------------------------
      const addPlayer = (state: PlayerState, sessionId: string) => {
        if (this.ships.has(sessionId)) return; // dedup guard
        const sprite = new ShipSprite(this, state, sessionId === this.localId);
        this.ships.set(sessionId, sprite);

        // Apply current dead state immediately — listen() only fires on future changes,
        // so a player who is already dead when we join would otherwise appear visible.
        if (state.isDead) sprite.setDead(true);

        if (sessionId === this.localId) {
          this.cameras.main.startFollow(sprite.image, true, 0.08, 0.08);
          // Remember whether we have a turret/beam weapon for reticle + fire trigger
          this.localIsBeam = PRIMARY_WEAPONS[(state.shipId as ShipId)]?.type === 'beam';
        }

        $(state).listen('x',           () => sprite.onServerUpdate(state));
        $(state).listen('y',           () => sprite.onServerUpdate(state));
        $(state).listen('isThrusting', () => {
          sprite.onServerUpdate(state);
          if (sessionId === this.localId) {
            if (state.isThrusting) {
              if (!this.thrusterSound?.isPlaying) this.thrusterSound?.play();
            } else {
              this.thrusterSound?.stop();
            }
          }
        });
        $(state).listen('rotation', () => sprite.onServerUpdate(state));
        $(state).listen('armor',  () => sprite.onArmorUpdate(state));
        $(state).listen('shield',   () => {
          sprite.onShieldUpdate(state);
          if (sessionId === this.localId) {
            const prev = this.localShield;
            const cur  = state.shield;
            if (prev >= 0) {
              if (cur <= 0 && prev > 0)  this.sound.play('sfx_shield_dn', { volume: 0.4 });
              if (cur > 0  && prev <= 0) this.sound.play('sfx_shield_up', { volume: 0.3 });
            }
            this.localShield = cur;
          }
        });
        $(state).listen('isDead',   () => {
          sprite.setDead(state.isDead);
          if (state.isDead) {
            const expKey = Math.random() < 0.5 ? 'sfx_explosion_0' : 'sfx_explosion_1';
            this.sound.play(expKey, { volume: 0.5 });
            if (sessionId === this.localId) {
              this.thrusterSound?.stop();
              this.cameras.main.stopFollow();
            }
          } else if (sessionId === this.localId) {
            this.cameras.main.startFollow(sprite.image, true, 0.08, 0.08);
            this.sound.play('sfx_twoTone', { volume: 0.4 });
          }
        });
        $(state).listen('rankIndex', () => sprite.setRankIndex(state.rankIndex ?? 0));
      };

      // ------------------------------------------------------------------
      // Projectile added
      // ------------------------------------------------------------------
      const addProjectile = (state: ProjectileState, id: string) => {
        if (this.projectiles.has(id)) return; // dedup guard
        const ownerState  = room.state.players.get(state.ownerSessionId);
        const shipId      = (ownerState?.shipId as ShipId) ?? 'fighter';
        const isSecondary = state.weaponType === 'torpedo' || state.weaponType === 'missile';
        const color       = isSecondary
          ? (SECONDARY_WEAPONS[shipId]?.color ?? 0xffffff)
          : (PRIMARY_WEAPONS[shipId]?.color   ?? 0xffffff);
        const sprite     = new ProjectileSprite(this, state, color, state.weaponType);
        this.projectiles.set(id, sprite);

        if (state.ownerSessionId === this.localId) {
          if (isSecondary) {
            const key = state.weaponType === 'missile' ? 'snd_missile_launch' : 'snd_torp_launch';
            this.sound.play(key, { volume: 0.3 });
          } else {
            const key = getShipVisual(shipId).primarySound;
            this.sound.play(key, { volume: 0.1 });
          }
        }

        // Update position + fade whenever the server sends a tick
        $(state).listen('x',                () => sprite.onServerUpdate(state));
        $(state).listen('distanceTraveled', () => sprite.onServerUpdate(state));
      };

      // ------------------------------------------------------------------
      // Beam flash (instant-hit weapon — visual only)
      // ------------------------------------------------------------------
      room.onMessage<BeamFiredPayload>(ServerEvents.BEAM_FIRED, (payload) => {
        this.drawBeam(payload);
        if (payload.ownerSessionId === this.localId) {
          const ownerShipId = room.state.players.get(payload.ownerSessionId)?.shipId ?? 'fighter';
          this.sound.play(getShipVisual(ownerShipId).primarySound, { volume: 0.1 });
        }
      });

      // ------------------------------------------------------------------
      // Shield hit flash (directional arc around the struck ship)
      // ------------------------------------------------------------------
      room.onMessage<ShieldHitPayload>(ServerEvents.SHIELD_HIT, (payload) => {
        this.ships.get(payload.targetSessionId)?.onShieldHit(payload.impactAngle);
        if (payload.targetSessionId === this.localId) {
          this.sound.play('sfx_shield_hit', { volume: 0.08 });
        }
      });

      // ------------------------------------------------------------------
      // Hull impact spark (armor takes a direct hit)
      // ------------------------------------------------------------------
      room.onMessage<HullHitPayload>(ServerEvents.HULL_HIT, (payload) => {
        this.ships.get(payload.targetSessionId)?.onHullHit(payload.impactAngle);
        if (payload.targetSessionId === this.localId) {
          this.sound.play('sfx_hull_hit', { volume: 0.08 });
        }
      });

      // ------------------------------------------------------------------
      // Collision sounds (local player only to avoid multiplayer noise)
      // ------------------------------------------------------------------
      room.onMessage<ShipCollisionPayload>(ServerEvents.SHIP_COLLISION, (payload) => {
        if (payload.sessionIdA === this.localId || payload.sessionIdB === this.localId) {
          this.sound.play('sfx_collision', { volume: 0.25 });
        }
      });

      room.onMessage<WallCollisionPayload>(ServerEvents.WALL_COLLISION, (payload) => {
        if (payload.sessionId === this.localId) {
          this.sound.play('sfx_collision', { volume: 0.25 });
        }
      });

      room.onMessage<PowerupCollectedPayload>(ServerEvents.POWERUP_COLLECTED, (payload) => {
        if (payload.collectorSessionId === this.localId) {
          this.sound.play('sfx_twoTone', { volume: 0.35 });
        }
      });

      // ------------------------------------------------------------------
      // Wire subscriptions once state has arrived
      // ------------------------------------------------------------------
      let subscribed = false;
      const wireSubscriptions = () => {
        if (subscribed || !room.state?.players) return;
        subscribed = true;

        $(room.state.players).onAdd(addPlayer, true);
        $(room.state.players).onRemove((_state: PlayerState, sessionId: string) => {
          this.ships.get(sessionId)?.destroy();
          this.ships.delete(sessionId);
        });

        $(room.state.projectiles).onAdd(addProjectile, true);
        $(room.state.projectiles).onRemove((_state: ProjectileState, id: string) => {
          this.projectiles.get(id)?.destroy();
          this.projectiles.delete(id);
        });

        $(room.state.stations).onAdd((state: SpaceStationState, id: string) => {
          if (this.stations.has(id)) return;
          this.stations.set(id, new StationSprite(this, state));
        }, true);
        $(room.state.stations).onRemove((_state: SpaceStationState, id: string) => {
          this.stations.get(id)?.destroy();
          this.stations.delete(id);
        });

        $(room.state.powerups).onAdd((state: PowerupState, id: string) => {
          if (this.powerups.has(id)) return;
          this.powerups.set(id, new PowerupSprite(this, state));
        }, true);
        $(room.state.powerups).onRemove((_state: PowerupState, id: string) => {
          this.powerups.get(id)?.destroy();
          this.powerups.delete(id);
        });
      };

      room.onStateChange(wireSubscriptions);
      wireSubscriptions(); // no-op if state not ready yet; fires on first patch

    }).catch((err: unknown) => {
      console.error('[RTTV] Server connection failed:', err);
      this.add.text(
        this.scale.width / 2, this.scale.height / 2,
        'Could not connect to server.\nIs the game server running?',
        {
          fontFamily: 'KenvectorFuture',
          fontSize: '14px',
          color: '#ff4444',
          align: 'center',
          backgroundColor: '#00000099',
          padding: { x: 20, y: 16 },
        }
      ).setOrigin(0.5).setScrollFactor(0).setDepth(200);
    });
  }

  // ---------------------------------------------------------------------------
  // Beam flash renderer
  // ---------------------------------------------------------------------------

  /**
   * Draw a tapered line flash for a beam shot.
   *
   * Each segment along the beam has progressively lower alpha (bright at
   * the muzzle, dark at the far end).  Two draw passes per segment give a
   * coloured outer glow + white inner core.  The whole graphic then fades
   * out and is destroyed via a tween.
   */
  /**
   * Draw a beam flash using just 3 overlapping lines at different widths/alphas.
   * The whole graphic tweens to alpha 0 in ~250 ms for the fade-out effect.
   * 3 draw commands instead of the previous 54 — same visual read.
   */
  private drawBeam(payload: BeamFiredPayload) {
    const dx  = payload.endX - payload.originX;
    const dy  = payload.endY - payload.originY;
    if (Math.hypot(dx, dy) < 1) return;

    const ox = payload.originX;
    const oy = payload.originY;
    const ex = payload.endX;
    const ey = payload.endY;

    const gfx = this.add.graphics();
    gfx.setDepth(15);

    // Wide soft glow
    gfx.lineStyle(8, payload.color, 0.18);
    gfx.lineBetween(ox, oy, ex, ey);
    // Mid tinted beam
    gfx.lineStyle(3, payload.color, 0.70);
    gfx.lineBetween(ox, oy, ex, ey);
    // Bright white core
    gfx.lineStyle(1, 0xffffff, 0.92);
    gfx.lineBetween(ox, oy, ex, ey);

    // Fade out and self-destruct
    this.tweens.add({
      targets:    gfx,
      alpha:      0,
      duration:   250,
      ease:       'Power2',
      onComplete: () => gfx.destroy(),
    });
  }

  // ---------------------------------------------------------------------------
  // Asteroid border (deterministic seeded layout)
  // ---------------------------------------------------------------------------

  private createAsteroidBorder() {
    let seed = 0xdeadbeef;
    const rand = () => {
      seed = Math.imul(seed ^ (seed >>> 17), 0x45d9f3b) >>> 0;
      seed = Math.imul(seed ^ (seed >>> 13), 0x45d9f3b) >>> 0;
      seed = (seed ^ (seed >>> 16)) >>> 0;
      return seed / 0x100000000;
    };

    const place = (cx: number, cy: number) => {
      const isSmall = rand() < 0.28;
      const texture = isSmall ? 'ritv_asteroid_small' : 'ritv_asteroid_large';
      const jitter = BORDER_THICKNESS * 0.35;
      const x      = cx + (rand() - 0.5) * jitter * 2;
      const y      = cy + (rand() - 0.5) * jitter * 2;
      const size   = isSmall ? 48 + rand() * 30 : 74 + rand() * 38;
      this.add.image(x, y, texture)
        .setDisplaySize(size, size)
        .setRotation(rand() * Math.PI * 2)
        .setAlpha(0.96)
        .setDepth(1);
    };

    const STEP = 80;
    const half  = BORDER_THICKNESS / 2;

    for (let x = 0; x <= WORLD_WIDTH; x += STEP) {
      place(x, half);
      place(x, WORLD_HEIGHT - half);
    }
    for (let y = STEP; y < WORLD_HEIGHT; y += STEP) {
      place(half, y);
      place(WORLD_WIDTH - half, y);
    }
  }

  // ---------------------------------------------------------------------------
  // Arena obstacle rendering
  // ---------------------------------------------------------------------------

  /**
   * Render static arena obstacles using the same generateObstacles() call as
   * the server — identical seed guarantees client visuals match server physics.
   *
   * arenaName defaults to 'The Void'; pass it via room metadata when multi-arena
   * support is added.
   */
  private createArenaObstacles(arenaName = 'The Void', obstacleDensity?: number) {
    const base   = ARENAS[arenaName] ?? ARENAS['The Void'];
    const config = obstacleDensity !== undefined ? { ...base, obstacleDensity } : base;
    const obstacles = generateObstacles(config);

    for (const obs of obstacles) {
      const isMeteor = obs.frame.startsWith('meteor') || obs.frame.startsWith('spaceMeteors');
      if (isMeteor) {
        const texture = obs.frame.includes('_med')
          ? 'ritv_asteroid_small'
          : obs.frame.startsWith('spaceMeteors')
            ? 'ritv_asteroid_giant'
            : 'ritv_asteroid_large';
        const sizeFactor = texture === 'ritv_asteroid_small'
          ? 3.3
          : texture === 'ritv_asteroid_giant'
            ? 2.25
            : 2.45;
        const size = obs.radius * sizeFactor;
        this.add.image(obs.x, obs.y, texture)
          .setDisplaySize(size, size)
          .setRotation(obs.rotation)
          .setDepth(3);
        continue;
      }

      this.add.image(obs.x, obs.y, 'ritv_derelict')
        .setDisplaySize(obs.radius * 2.4, obs.radius * 2.4)
        .setRotation(obs.rotation)
        .setDepth(3);
    }
  }

  // ---------------------------------------------------------------------------
  // Update loop
  // ---------------------------------------------------------------------------

  override update(time: number, delta: number) {
    // Guard against Vite HMR hot-replace before create() re-runs
    if (!this.connected || !this.keyW) return;

    const localShip = this.ships.get(this.localId);
    const roundPhase = gameClient.room?.state?.roundPhase;
    const canFire    = roundPhase === 'playing';

    let aimAngle: number;
    let thrust: boolean, brake: boolean, rotateLeft: boolean, rotateRight: boolean;
    let firing: boolean, firingSecondary: boolean;

    if (IS_TOUCH) {
      // On touch devices: movement from virtual joystick, fire from buttons.
      // Aim angle follows the ship's own facing direction (fire forward).
      aimAngle      = localShip ? localShip.image.rotation : 0;
      thrust        = touchState.thrust;
      brake         = touchState.brake;
      rotateLeft    = touchState.rotateLeft;
      rotateRight   = touchState.rotateRight;
      firing        = canFire && touchState.primaryFire;
      firingSecondary = canFire && touchState.secondaryFire;
    } else {
      // Desktop: mouse + keyboard
      const pointer = this.input.activePointer;
      aimAngle = localShip
        ? Math.atan2(
            pointer.worldX - localShip.image.x,
            -(pointer.worldY - localShip.image.y),
          )
        : 0;
      thrust        = this.keyW.isDown;
      brake         = this.keyS.isDown;
      rotateLeft    = this.keyA.isDown;
      rotateRight   = this.keyD.isDown;
      firing        = canFire && (this.keySpace.isDown || (pointer.isDown && pointer.leftButtonDown()));
      firingSecondary = canFire && (this.keyF.isDown   || (pointer.isDown && pointer.rightButtonDown()));
    }

    const input: InputMessage = {
      seq:            ++this.inputSeq,
      thrust,
      brake,
      rotateLeft,
      rotateRight,
      fire:           firing,
      fireSecondary:  firingSecondary,
      weaponSlot:     0,
      aimAngle,
    };

    gameClient.sendInput(input);
    if (localShip) localShip.simulateInput(input, delta / 1000);

    // Drive local flame directly from keyboard — no server roundtrip needed
    if (localShip) localShip.setThrusting(input.thrust);

    // --- Aim reticle (beam ships only, redrawn only when angle changes) ---
    if (this.localIsBeam && localShip && !localShip.isDead) {
      // ~0.5° threshold — avoids rebuilding geometry every frame for micro-jitter
      if (this.aimDirty || Math.abs(aimAngle - this.lastAimAngle) > 0.009) {
        this.aimDirty     = false;
        this.lastAimAngle = aimAngle;

        const ox  = localShip.image.x;
        const oy  = localShip.image.y;
        const len = 680;
        const ex  = ox + Math.sin(aimAngle) * len;
        const ey  = oy - Math.cos(aimAngle) * len;

        this.aimReticle.clear();
        // Soft outer glow line
        this.aimReticle.lineStyle(4, 0xff3333, 0.12);
        this.aimReticle.lineBetween(ox, oy, ex, ey);
        // Sharp inner line, fading toward the tip via alpha on the whole object
        this.aimReticle.lineStyle(1, 0xff3333, 0.45);
        this.aimReticle.lineBetween(ox, oy, ex, ey);
        // Cursor dot (desktop only — no pointer world position on touch)
        if (!IS_TOUCH) {
          const p = this.input.activePointer;
          this.aimReticle.fillStyle(0xff3333, 0.65);
          this.aimReticle.fillCircle(p.worldX, p.worldY, 3);
        }
      }
    } else if (this.aimDirty === false) {
      // Ship just died or left — clear once
      this.aimReticle.clear();
      this.aimDirty = true;
    }

    this.stations.forEach((sprite)    => sprite.update(delta));
    this.ships.forEach((sprite)       => sprite.update(delta));
    this.projectiles.forEach((sprite) => sprite.update(delta));
    this.powerups.forEach((sprite)    => sprite.update(time));
  }
}
